import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaEnvelope, FaTrash, FaCheckCircle, FaClock, FaPaperPlane } from 'react-icons/fa';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormatter';
import { useDateTimeSettings } from '../hooks/useDateTimeSettings';
import ConfirmDialog from './ConfirmDialog';
import { getContextualError } from '../utils/errorMessages';

/**
 * Communication History Modal Component
 * Displays message thread with student, allows sending new messages, and deleting messages
 */
function CommunicationHistoryModal({ isOpen, onClose, studentId, studentName }) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const messagesEndRef = useRef(null);
  const dateTimeSettings = useDateTimeSettings();

  useEffect(() => {
    if (isOpen && studentId) {
      fetchMessageHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, studentId]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchMessageHistory = async () => {
    try {
      setIsLoading(true);
      const response = await HttpInterceptor.get(`/api/messages/trainer/student/${studentId}`);
      // Messages are already sorted by date (oldest first) from backend
      const messages = response.data?.messages || response.data?.data || [];
      setMessages(messages);
    } catch (error) {
      console.error('Error fetching message history:', error);
      // Don't show error if endpoint doesn't exist yet (404) - just show empty state
      if (error.response?.status !== 404) {
        const friendlyError = getContextualError('fetch messages', error);
        toast.error(friendlyError);
      }
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setIsSending(true);
      await HttpInterceptor.post('/api/messages/trainer/send', {
        studentId: studentId,
        content: newMessage.trim(),
        type: 'message',
      });

      toast.success('Message sent successfully');
      setNewMessage('');
      // Refresh message history
      await fetchMessageHistory();
    } catch (error) {
      console.error('Send message error:', error);
      const friendlyError = getContextualError('send message', error);
      toast.error(friendlyError);
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    setShowDeleteConfirm(messageId);
  };

  const confirmDeleteMessage = async () => {
    const messageId = showDeleteConfirm;
    if (!messageId) return;

    try {
      setIsDeleting(messageId);
      await HttpInterceptor.delete(`/api/messages/${messageId}`);
      toast.success('Message deleted successfully');
      setShowDeleteConfirm(null);
      // Refresh message history
      await fetchMessageHistory();
    } catch (error) {
      console.error('Delete message error:', error);
      const friendlyError = getContextualError('delete message', error);
      toast.error(friendlyError);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  // Group messages by date
  const groupedMessages = messages.reduce((acc, message) => {
    const date = new Date(message.createdAt || message.date || message.timestamp);
    const dateKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(message);
    return acc;
  }, {});

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="communication-history-title"
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <div>
            <h2 id="communication-history-title" className="text-2xl font-bold text-gray-900">
              Communication History
            </h2>
            <p className="text-sm text-gray-600 mt-1">{studentName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-2"
            aria-label="Close communication history modal"
          >
            <FaTimes className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading message history...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <FaEnvelope className="text-6xl text-gray-300 mx-auto mb-4" aria-hidden="true" />
              <p className="text-gray-600 text-lg font-medium">No messages yet</p>
              <p className="text-gray-500 text-sm mt-2">Start a conversation with {studentName}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedMessages).map(([dateKey, dateMessages]) => (
                <div key={dateKey} className="space-y-4">
                  {/* Date Header */}
                  <div className="flex items-center justify-center">
                    <div className="bg-gray-100 px-3 py-1 rounded-full">
                      <span className="text-xs font-medium text-gray-600">{dateKey}</span>
                    </div>
                  </div>

                  {/* Messages for this date */}
                  {dateMessages.map((message) => {
                    const isTrainer = message.senderType === 'trainer' || message.from === 'trainer' || !message.from;
                    
                    return (
                      <div
                        key={message._id || message.id}
                        className={`flex ${isTrainer ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-lg p-3 ${
                            isTrainer
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{message.content || message.message}</p>
                          <div className={`flex items-center justify-between mt-2 text-xs ${
                            isTrainer ? 'text-blue-100' : 'text-gray-500'
                          }`}>
                            <span>
                              {formatDateTime(
                                message.createdAt || message.date || message.timestamp,
                                dateTimeSettings.dateFormat,
                                dateTimeSettings.timeFormat
                              )}
                            </span>
                            {message.read && (
                              <FaCheckCircle className="ml-2" aria-label="Message read" />
                            )}
                          </div>
                          {isTrainer && (
                            <button
                              onClick={() => handleDeleteMessage(message._id || message.id)}
                              disabled={isDeleting === (message._id || message.id)}
                              className="mt-2 text-xs text-blue-100 hover:text-white focus:outline-none focus:ring-2 focus:ring-white rounded px-2 py-1"
                              aria-label={`Delete message sent at ${formatDateTime(
                                message.createdAt || message.date || message.timestamp,
                                dateTimeSettings.dateFormat,
                                dateTimeSettings.timeFormat
                              )}`}
                            >
                              {isDeleting === (message._id || message.id) ? (
                                'Deleting...'
                              ) : (
                                <>
                                  <FaTrash className="inline mr-1" aria-hidden="true" />
                                  Delete
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="border-t border-gray-200 p-3 md:p-4 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-2">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here..."
              className="flex-1 px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none min-h-[60px] md:min-h-0"
              rows={3}
              aria-label="Message input"
              aria-describedby="message-help"
            />
            <button
              onClick={handleSendMessage}
              disabled={isSending || !newMessage.trim()}
              className="px-4 md:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              aria-label="Send message"
            >
              {isSending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <FaPaperPlane aria-hidden="true" />
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
          <p id="message-help" className="text-xs text-gray-500 mt-1">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <ConfirmDialog
          isOpen={!!showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(null)}
          onConfirm={confirmDeleteMessage}
          title="Delete Message"
          message="Are you sure you want to delete this message? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />
      )}
    </div>
  );
}

export default CommunicationHistoryModal;

